import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Upload, X, Image as ImageIcon, ArrowsClockwise, DownloadSimple, CheckCircle } from "@phosphor-icons/react";

/**
 * PhotoUploader — file upload + webcam capture, uploads via /api/uploads/image,
 * returns URL via onChange. Value can be an absolute URL or /api/uploads/*.
 */
export default function PhotoUploader({ value, onChange, kind = "avatar", size = 80, shape = "circle", label }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("file"); // file | camera
  const [preview, setPreview] = useState(null); // data URL preview
  const [uploading, setUploading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const shownUrl = value ? (value.startsWith("http") || value.startsWith("data:")
    ? value : `${API_BASE.replace("/api", "")}${value}`) : null;

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  };

  useEffect(() => () => stopCamera(), []);
  useEffect(() => { if (!open) { stopCamera(); setPreview(null); } }, [open]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStreaming(true);
    } catch (e) {
      toast.error("Impossible d'accéder à la caméra : " + e.message);
    }
  };

  const capture = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    setPreview(dataUrl);
    stopCamera();
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image trop volumineuse (max 5 Mo)");
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!preview) return toast.error("Aucune image sélectionnée");
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/image", { data_url: preview, kind });
      onChange(data.url);
      toast.success("Image téléversée");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const clear = () => { onChange(""); toast.success("Photo retirée"); };

  const download = () => {
    if (!shownUrl) return;
    // Preserve the original filename from the URL when possible
    const fname = (value.split("/").pop() || `kosyam-${kind}.png`).split("?")[0];
    const a = document.createElement("a");
    a.href = shownUrl;
    a.download = fname;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const rounded = shape === "circle" ? "rounded-full" : "rounded-lg";

  return (
    <div className="flex items-center gap-3">
      <div
        className={`${rounded} bg-muted overflow-hidden flex items-center justify-center border border-border relative group`}
        style={{ width: size, height: size }}
        title={shownUrl ? "Cliquer pour télécharger l'image existante" : ""}
      >
        {shownUrl ? (
          <>
            <a href={shownUrl} download target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); download(); }} className="block w-full h-full" data-testid="photo-download-link">
              <img src={shownUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
            </a>
            {/* Existence badge — small checkmark bottom-right */}
            <div className="absolute -bottom-1 -right-1 bg-accent text-accent-foreground rounded-full p-0.5 shadow-md" title="Image téléversée" data-testid="photo-exists-badge">
              <CheckCircle size={16} weight="fill" />
            </div>
            {/* Hover overlay with download icon */}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
              <DownloadSimple size={22} weight="bold" className="text-white" />
            </div>
          </>
        ) : (
          <ImageIcon size={Math.floor(size / 3)} weight="duotone" className="text-muted-foreground" />
        )}
      </div>
      <div className="flex flex-col gap-1">
        {label && <div className="text-xs text-muted-foreground">{label}</div>}
        {value && (
          <div className="text-xs text-accent flex items-center gap-1 font-medium" data-testid="photo-exists-label">
            <CheckCircle size={12} weight="fill" /> Existant
          </div>
        )}
        <div className="flex gap-1 flex-wrap">
          <Button type="button" size="sm" variant="outline" onClick={() => { setTab("file"); setOpen(true); }} data-testid="btn-photo-upload">
            <Upload size={14} className="mr-1" /> {value ? "Remplacer" : "Téléverser"}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => { setTab("camera"); setOpen(true); }} data-testid="btn-photo-camera">
            <Camera size={14} className="mr-1" /> Caméra
          </Button>
          {value && (
            <>
              <Button type="button" size="sm" variant="outline" onClick={download} data-testid="btn-photo-download" title="Télécharger l'image existante">
                <DownloadSimple size={14} />
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid="btn-photo-clear" title="Retirer">
                <X size={14} />
              </Button>
            </>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-photo">
          <DialogHeader><DialogTitle>{tab === "file" ? "Téléverser une image" : "Prendre une photo"}</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-3">
            <Button type="button" size="sm" variant={tab === "file" ? "default" : "outline"} onClick={() => { stopCamera(); setTab("file"); }}>Fichier</Button>
            <Button type="button" size="sm" variant={tab === "camera" ? "default" : "outline"} onClick={() => setTab("camera")}>Caméra</Button>
          </div>

          {tab === "file" && (
            <div className="space-y-3">
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-accent transition-colors"
              >
                <Upload size={28} weight="duotone" className="mx-auto text-muted-foreground mb-2" />
                <div className="text-sm">Cliquez pour choisir une image</div>
                <div className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP · max 5 Mo</div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="input-photo-file" />
              {preview && (
                <img src={preview} alt="preview" className="w-full max-h-64 object-contain rounded-md border border-border" />
              )}
            </div>
          )}

          {tab === "camera" && (
            <div className="space-y-3">
              {!streaming && !preview && (
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                  <Camera size={36} weight="duotone" className="mx-auto text-muted-foreground mb-2" />
                  <div className="text-sm mb-3">Activez votre caméra pour prendre une photo</div>
                  <Button type="button" onClick={startCamera} data-testid="btn-camera-start">
                    <Camera size={14} className="mr-1" /> Activer caméra
                  </Button>
                </div>
              )}
              {streaming && (
                <>
                  <video ref={videoRef} className="w-full rounded-md border border-border bg-black" playsInline muted />
                  <div className="flex gap-2 justify-center">
                    <Button type="button" onClick={capture} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-camera-capture">
                      <Camera size={14} className="mr-1" /> Capturer
                    </Button>
                    <Button type="button" variant="outline" onClick={stopCamera}>Arrêter</Button>
                  </div>
                </>
              )}
              {preview && !streaming && (
                <>
                  <img src={preview} alt="preview" className="w-full max-h-64 object-contain rounded-md border border-border" />
                  <Button type="button" variant="outline" size="sm" onClick={() => { setPreview(null); startCamera(); }}>
                    <ArrowsClockwise size={14} className="mr-1" /> Reprendre
                  </Button>
                </>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="button" onClick={save} disabled={!preview || uploading} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-photo-save">
              {uploading ? "Téléversement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
